import classNames from 'classnames';
import { format, isToday, isYesterday, subDays, subMonths } from 'date-fns';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { DefaultRootState, useSelector } from 'react-redux';

import { SvgIconUrl } from 'common/components';
import { DateRangePicker } from 'common/components/DateRangePicker/DateRangePicker.component';
import { getFormattedRange, getLocaleAsync } from 'common/components/DateRangePicker/utils';
import { AlignType } from 'common/components/Tooltip/types';

import { useI18n } from 'common/hooks';
import svgIcons from 'common/svgIcons';

import { MAX_STATEMENT_RANGE_DAYS } from '../constants';
import '../styles/statement-period.css';
import { StatementPeriodProps } from '../types';

const DEFAULT_STATEMENT_PERIOD_ALIGNMENT: AlignType = {
  offset: ['0%', '-60%'],
};

export const StatementPeriod: React.FC<React.PropsWithChildren<StatementPeriodProps>> = React.memo(
  ({ value, onChange, onDateReset, disabled, placement = 'bottom', align = DEFAULT_STATEMENT_PERIOD_ALIGNMENT }) => {
    const { translate } = useI18n();

    const [locale, setLocale] = useState<Locale | undefined>();

    const culture = useSelector((state: DefaultRootState) => state.common.resources.culture);

    const onDateChange = useCallback(
      (period) => {
        if (period) onChange(period);
      },
      [onChange],
    );

    useEffect(() => {
      getLocaleAsync(setLocale);
    }, [culture]);

    const today = useMemo(() => new Date(), []);
    const firstDateOfMonth = useMemo(() => format(today, 'yyyy-MM-01'), [today]);

    const previousMonthStart = useMemo(
      () => subMonths(new Date(firstDateOfMonth), 1).toDateString(),
      [firstDateOfMonth],
    );
    const previousMonthEnd = useMemo(() => subDays(new Date(firstDateOfMonth), 1).toDateString(), [firstDateOfMonth]);

    const currentMonthStart = useMemo(() => new Date(firstDateOfMonth).toDateString(), [firstDateOfMonth]);
    const currentMonthEnd = useMemo(() => today.toDateString(), [today]);

    const monthBeforeStart = useMemo(() => new Date(subMonths(today, 1)).toDateString(), [today]);

    const isPrevMonth = useMemo(
      () => value.startDate.toDateString() === previousMonthStart && value.endDate.toDateString() === previousMonthEnd,
      [previousMonthEnd, previousMonthStart, value.endDate, value.startDate],
    );
    const isThisMonth = useMemo(
      () => value.startDate.toDateString() === currentMonthStart && value.endDate.toDateString() === currentMonthEnd,
      [currentMonthEnd, currentMonthStart, value.endDate, value.startDate],
    );
    const isLastMonth = useMemo(
      () =>
        value.startDate.toDateString() === monthBeforeStart && value.endDate.toDateString() === today.toDateString(),
      [monthBeforeStart, today, value.endDate, value.startDate],
    );
    const isTodayDay = useMemo(
      () => isToday(value.startDate) && isToday(value.endDate),
      [value.endDate, value.startDate],
    );
    const isYesterdayDay = useMemo(
      () => isYesterday(value.startDate) && isYesterday(value.endDate),
      [value.endDate, value.startDate],
    );

    const renderDateLabel = useCallback(() => {
      if (isPrevMonth) return translate('mobile:headlines:prevMonth');
      if (isThisMonth) return translate('mobile:headlines:thisMonth');
      if (isLastMonth) return translate('mobile:headlines:lastMonth');
      if (isTodayDay) return translate('mobile:headlines:today');
      if (isYesterdayDay) return translate('mobile:headlines:yesterday');

      return getFormattedRange(locale, value.startDate, value.endDate);
    }, [
      isLastMonth,
      isPrevMonth,
      isThisMonth,
      isTodayDay,
      isYesterdayDay,
      locale,
      translate,
      value.endDate,
      value.startDate,
    ]);

    const statementLabelClassName = classNames({
      Body4_small_reg_12: true,
      'statement__field-hint': true,
      'statement__field-hint--disabled': disabled,
    });

    const statementFieldClassName = classNames({
      statement__field: true,
      'statement__field--disabled': disabled,
    });

    return (
      <DateRangePicker
        value={value}
        onChange={onDateChange}
        onReset={onDateReset}
        placement={placement}
        align={align}
        maxDays={MAX_STATEMENT_RANGE_DAYS}
      >
        <div className={statementFieldClassName}>
          <span className={statementLabelClassName}>{translate('mobile:statement:period:placeholder')}</span>
          <div className='statement__field-value Body3_reg_16'>{renderDateLabel()}</div>
          <SvgIconUrl className='statement__field-icon' symbol={svgIcons.Calendar()} />
        </div>
      </DateRangePicker>
    );
  },
);
